/**
 * Applies document head SEO for the current route (SPA-friendly, no react-helmet).
 */
import { useEffect } from 'react';
import {
  absoluteUrl,
  DEFAULT_OG_IMAGE,
  DEFAULT_LOCALE,
  SITE_NAME,
  SITE_URL,
  THEME_COLOR,
  type PageSeo,
} from './seoConfig';
import { buildJsonLdGraph } from './jsonLd';

const META_MANAGED = 'data-geovera-seo';
const JSON_LD_ID = 'geovera-json-ld';

function setMeta(attr: 'name' | 'property', key: string, content: string | undefined) {
  if (!content) return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(META_MANAGED, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string, extra?: Record<string, string>) {
  const hreflang = extra?.hrefLang || extra?.hreflang;
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(META_MANAGED, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
  }
}

function setJsonLd(data: Record<string, unknown>) {
  let el = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = JSON_LD_ID;
    el.type = 'application/ld+json';
    el.setAttribute(META_MANAGED, '1');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Update title, description, robots, Open Graph, Twitter, canonical, and JSON-LD.
 */
export function applyPageSeo(page: PageSeo) {
  const url = absoluteUrl(page.path);
  const image = page.ogImage || DEFAULT_OG_IMAGE;
  const robots = page.robots || 'index, follow, max-image-preview:large, max-snippet:-1';

  document.title = page.title;

  setMeta('name', 'description', page.description);
  if (page.keywords) setMeta('name', 'keywords', page.keywords);
  setMeta('name', 'robots', robots);
  setMeta('name', 'googlebot', robots);
  setMeta('name', 'theme-color', THEME_COLOR);
  setMeta('name', 'application-name', SITE_NAME);
  setMeta('name', 'author', SITE_NAME);
  setMeta('name', 'publisher', SITE_NAME);
  setMeta('name', 'geo.region', 'IN');
  setMeta('name', 'language', 'English');

  // Open Graph
  setMeta('property', 'og:type', page.ogType || 'website');
  setMeta('property', 'og:site_name', SITE_NAME);
  setMeta('property', 'og:locale', DEFAULT_LOCALE);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:title', page.title);
  setMeta('property', 'og:description', page.description);
  setMeta('property', 'og:image', image);
  setMeta('property', 'og:image:alt', `${SITE_NAME} — ${page.title}`);

  // Twitter
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:url', url);
  setMeta('name', 'twitter:title', page.title);
  setMeta('name', 'twitter:description', page.description);
  setMeta('name', 'twitter:image', image);
  setMeta('name', 'twitter:image:alt', page.title);

  setLink('canonical', url);
  setLink('alternate', url, { hrefLang: 'en-IN' });
  setLink('alternate', url, { hrefLang: 'x-default' });

  // Prefer brand origin in share previews
  setMeta('name', 'twitter:domain', SITE_URL.replace(/^https?:\/\//, ''));

  setJsonLd(buildJsonLdGraph(page));
}

export function usePageSeo(page: PageSeo) {
  useEffect(() => {
    applyPageSeo(page);
  }, [
    page.path,
    page.title,
    page.description,
    page.robots,
    page.ogType,
    page.ogImage,
    page.keywords,
  ]);
}

export default usePageSeo;
